"use client";

import {
  useImagePrefetch,
  type PrefetchImage,
} from "@/components/global/utils/imagePrefetch";
import { GALLERY_SLOT_COUNT } from "@/components/bastian-keller/Gallery";

import { PageBastianKellerSchema } from "@/sanity/pages/PageBastianKellerTemplate";

/**
 * Every lazily loaded image on the page, listed in the order it is scrolled onto.
 *
 * The hero portrait and video placeholder are deliberately absent: they render
 * with `priority`, so they are already in flight before any of this runs, and the
 * hero chip is on screen from the start. Everything else is below the fold, and
 * most of it is revealed by scroll position rather than on arrival — see
 * useImagePrefetch for why that is the case worth prefetching for.
 */

/** The quality the photos are rendered at; the icons are all rendered at the default. */
const PHOTO_QUALITY = 100;

function pageImages(data: PageBastianKellerSchema): PrefetchImage[] {
  return [
    { image: data?.heroSignature, quality: PHOTO_QUALITY },

    { image: data?.goalsChip?.icon },

    ...(data?.timelineItems || []).flatMap((item) => [
      { image: item?.chip?.icon },
      { image: item?.image, quality: PHOTO_QUALITY },
    ]),

    { image: data?.successChip?.icon },

    { image: data?.galleryChip?.icon },
    ...(data?.galleryItems || [])
      .slice(0, GALLERY_SLOT_COUNT)
      .map((item) => ({ image: item?.image, quality: PHOTO_QUALITY })),

    { image: data?.missionChip?.icon },
    { image: data?.missionImage, quality: PHOTO_QUALITY },
    { image: data?.missionSignature, quality: PHOTO_QUALITY },
  ];
}

export default function ImagePrefetch({ data }: { data: PageBastianKellerSchema }) {
  useImagePrefetch(pageImages(data));

  return null;
}
